// Global Instructions Rule Applied!
//
// Agent Access — Phase 3 (consent + grant lifecycle) HTTP service.
//
// Talks to the Python backend only (CLAUDE.md — never Supabase directly):
//   GET    /agent/grants                  my grants (active + revoked history)
//   POST   /agent/grants/:grantId/update  edit tools / modes / expiry in place
//   DELETE /agent/grants/:grantId         revoke (per-user kill switch)
//
// There is no create call here. A grant is born in exactly one place — the
// gateway's OAuth consent page, collected mid-handshake when the user
// connects from their assistant (agent_gateway/oauth_provider.py). The
// backend's POST /agent/grants still exists for that caller; this screen is
// the lifecycle surface (see, edit, revoke).
//
// Every response is strict-validated with its Zod schema before it reaches
// UI state (a response that fails validation is an error, not coerced). The
// backend RPCs force user_id = auth.uid() — there is deliberately no user
// parameter anywhere on this surface.

import { API_ROUTES, buildFullUrl, getBackendBaseUrl } from '../../../core/utils/endpoints.ts'
import {
  AgentGrant,
  AgentProduct,
  AgentProposal,
  validateAgentGrantsList,
  validateAgentGrantUpdated,
  validateAgentGrantRevoked,
  validateAgentProductsList,
  validateAgentProductEnabled,
  validateAgentProposalsList,
  validateAgentProposalReviewed,
  validateAgentProposalUndone,
  AgentActivitySummary,
  validateAgentActivitySummary
} from '../../../state_manager/data_models/agent_access_model.ts'
import { readJsonBody } from '../../../core/utils/utilities.ts'

const BACKEND_BASE_URL = getBackendBaseUrl()

export interface AgentGrantsResponse {
  data: AgentGrant[]
  error?: string
}

// No agent_id: the grant id in the URL fixes the agent, so an edit can never
// repoint a grant at a different product.
export interface AgentGrantScopePayload {
  tools: string[]
  expires_at: string
  // Phase 4.5: the subset of `tools` the user chose to run without approval.
  // Validated server-side against both the consented list and cfg.
  live_tools?: string[]
}

const jsonHeaders = (accessToken: string | null): Record<string, string> => {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`
  return headers
}

export const fetchMyAgentGrants = async (
  accessToken: string | null = null
): Promise<AgentGrantsResponse> => {
  try {
    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.AGENT_ACCESS.GRANTS_LIST),
      { method: API_ROUTES.AGENT_ACCESS.GRANTS_LIST.method, headers: jsonHeaders(accessToken) }
    )

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      throw new Error(`Failed to fetch agent grants: ${response.status} ${response.statusText}`)
    }

    const json = (await response.json()) as { data?: unknown }
    if (json.data == null) return { data: [] }
    return { data: validateAgentGrantsList(json.data).grants }
  } catch (error) {
    return {
      data: [],
      error: error instanceof Error ? error.message : 'An unexpected error occurred'
    }
  }
}

export const updateAgentGrant = async (
  grantId: string,
  payload: AgentGrantScopePayload,
  accessToken: string | null = null
): Promise<{ data: { agent_grants__id: string } | null; error?: string }> => {
  try {
    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.AGENT_ACCESS.GRANTS_UPDATE, { grantId }),
      {
        method: API_ROUTES.AGENT_ACCESS.GRANTS_UPDATE.method,
        headers: jsonHeaders(accessToken),
        body: JSON.stringify({ data: payload })
      }
    )

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      const body = await readJsonBody<{ message?: string }>(response)
      throw new Error(
        body?.message || `Failed to update access: ${response.status} ${response.statusText}`
      )
    }

    const json = (await response.json()) as { data?: unknown }
    if (json.data == null) return { data: null, error: 'Empty response from backend' }
    return { data: validateAgentGrantUpdated(json.data) }
  } catch (error) {
    return {
      data: null,
      error: error instanceof Error ? error.message : 'An unexpected error occurred'
    }
  }
}

export const revokeAgentGrant = async (
  grantId: string,
  accessToken: string | null = null
): Promise<{ success: boolean; error?: string }> => {
  try {
    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.AGENT_ACCESS.GRANTS_REVOKE, { grantId }),
      { method: API_ROUTES.AGENT_ACCESS.GRANTS_REVOKE.method, headers: jsonHeaders(accessToken) }
    )

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      const body = await readJsonBody<{ message?: string }>(response)
      throw new Error(
        body?.message || `Failed to revoke grant: ${response.status} ${response.statusText}`
      )
    }

    const json = (await response.json()) as { data?: unknown }
    if (json.data != null) validateAgentGrantRevoked(json.data)
    return { success: true }
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'An unexpected error occurred'
    }
  }
}

// ── Agent products (admin tab) ──────────────────────────────────────────────

export const fetchAgentProducts = async (
  accessToken: string | null = null
): Promise<{ data: AgentProduct[]; error?: string }> => {
  try {
    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.AGENT_ACCESS.PRODUCTS_LIST),
      { method: API_ROUTES.AGENT_ACCESS.PRODUCTS_LIST.method, headers: jsonHeaders(accessToken) }
    )

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      throw new Error(`Failed to fetch agent products: ${response.status} ${response.statusText}`)
    }

    const json = (await response.json()) as { data?: unknown }
    if (json.data == null) return { data: [] }
    return { data: validateAgentProductsList(json.data).products }
  } catch (error) {
    return {
      data: [],
      error: error instanceof Error ? error.message : 'An unexpected error occurred'
    }
  }
}

// ── Agent proposals (Phase 4 — propose-approve writes) ──────────────────────

export const fetchMyAgentProposals = async (
  accessToken: string | null = null
): Promise<{ data: AgentProposal[]; error?: string }> => {
  try {
    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.AGENT_ACCESS.PROPOSALS_LIST),
      { method: API_ROUTES.AGENT_ACCESS.PROPOSALS_LIST.method, headers: jsonHeaders(accessToken) }
    )

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      throw new Error(`Failed to fetch agent proposals: ${response.status} ${response.statusText}`)
    }

    const json = (await response.json()) as { data?: unknown }
    if (json.data == null) return { data: [] }
    return { data: validateAgentProposalsList(json.data).proposals }
  } catch (error) {
    return {
      data: [],
      error: error instanceof Error ? error.message : 'An unexpected error occurred'
    }
  }
}

const EMPTY_ACTIVITY_SUMMARY: AgentActivitySummary = {
  prog__reads_today: 0,
  prog__rows_returned_today: 0,
  prog__writes_this_week: 0,
  prog__pending_review: 0
}

export const fetchAgentActivitySummary = async (
  accessToken: string | null = null
): Promise<{ data: AgentActivitySummary; error?: string }> => {
  try {
    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.AGENT_ACCESS.ACTIVITY_SUMMARY),
      { method: API_ROUTES.AGENT_ACCESS.ACTIVITY_SUMMARY.method, headers: jsonHeaders(accessToken) }
    )

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      throw new Error(`Failed to fetch agent activity summary: ${response.status} ${response.statusText}`)
    }

    const json = (await response.json()) as { data?: unknown }
    if (json.data == null) return { data: EMPTY_ACTIVITY_SUMMARY }
    return { data: validateAgentActivitySummary(json.data).summary }
  } catch (error) {
    return {
      data: EMPTY_ACTIVITY_SUMMARY,
      error: error instanceof Error ? error.message : 'An unexpected error occurred'
    }
  }
}

export const reviewAgentProposal = async (
  proposalId: string,
  action: 'approve' | 'reject',
  accessToken: string | null = null,
  // The record the approver actually approved, when they edited it in the
  // review form. Omitted = approve exactly as the agent filed it.
  payload: Record<string, unknown> | null = null
): Promise<{
  success: boolean
  status?: string
  // The finalized outcome (row refs on success, {error, error_type, ...} on a
  // failed replay) — the caller's toast names the reason from it.
  result?: Record<string, unknown> | null
  error?: string
}> => {
  try {
    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.AGENT_ACCESS.PROPOSALS_REVIEW, { proposalId }),
      {
        method: API_ROUTES.AGENT_ACCESS.PROPOSALS_REVIEW.method,
        headers: jsonHeaders(accessToken),
        body: JSON.stringify({ data: payload ? { action, payload } : { action } })
      }
    )

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      const body = await readJsonBody<{ message?: string }>(response)
      throw new Error(
        body?.message || `Failed to review proposal: ${response.status} ${response.statusText}`
      )
    }

    const json = (await response.json()) as { data?: unknown }
    if (json.data == null) return { success: false, error: 'Empty response from backend' }
    const reviewed = validateAgentProposalReviewed(json.data)
    const rawResult = (reviewed as { result?: unknown }).result
    const result =
      rawResult && typeof rawResult === 'object' ? (rawResult as Record<string, unknown>) : null
    return { success: true, status: reviewed.agent_proposals__status, result }
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'An unexpected error occurred'
    }
  }
}

// Re-approve a FAILED proposal. Same response shape as an approve: the
// finalized status plus the outcome (row refs, or the new failure).
export const retryAgentProposal = async (
  proposalId: string,
  accessToken: string | null = null,
  // A corrected record from the review form. Omitted = replay unchanged, which
  // is right only when the failure was ours rather than the payload's.
  payload: Record<string, unknown> | null = null
): Promise<{
  success: boolean
  status?: string
  result?: Record<string, unknown> | null
  error?: string
}> => {
  try {
    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.AGENT_ACCESS.PROPOSALS_RETRY, { proposalId }),
      {
        method: API_ROUTES.AGENT_ACCESS.PROPOSALS_RETRY.method,
        headers: jsonHeaders(accessToken),
        body: JSON.stringify({ data: payload ? { payload } : {} })
      }
    )

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      const body = await readJsonBody<{ message?: string }>(response)
      throw new Error(body?.message || `Failed to retry proposal: ${response.status} ${response.statusText}`)
    }

    const json = (await response.json()) as { data?: unknown }
    if (json.data == null) return { success: false, error: 'Empty response from backend' }
    const reviewed = validateAgentProposalReviewed(json.data)
    const rawResult = (reviewed as { result?: unknown }).result
    const result =
      rawResult && typeof rawResult === 'object' ? (rawResult as Record<string, unknown>) : null
    return { success: true, status: reviewed.agent_proposals__status, result }
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'An unexpected error occurred'
    }
  }
}

// Resolves { success, residue }: residue lists the records the backend proved
// were NOT this write's (or would not delete) and left in place. Empty on a
// clean undo. A refused PRIMARY record is a failure (409), not residue.
export const undoAgentWrite = async (
  proposalId: string,
  accessToken: string | null = null
): Promise<{ success: boolean; residue?: string[]; error?: string }> => {
  try {
    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.AGENT_ACCESS.PROPOSALS_UNDO, { proposalId }),
      {
        method: API_ROUTES.AGENT_ACCESS.PROPOSALS_UNDO.method,
        headers: jsonHeaders(accessToken)
      }
    )

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      const body = await readJsonBody<{ message?: string }>(response)
      throw new Error(
        body?.message || `Failed to undo: ${response.status} ${response.statusText}`
      )
    }

    const json = (await response.json()) as { data?: unknown }
    if (json.data == null) throw new Error('Undo returned no outcome')
    const undone = validateAgentProposalUndone(json.data)
    return { success: true, residue: undone.undo_residue ?? [] }
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'An unexpected error occurred'
    }
  }
}

export const setAgentProductEnabled = async (
  agentKey: string,
  enabled: boolean,
  accessToken: string | null = null
): Promise<{ success: boolean; error?: string }> => {
  try {
    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.AGENT_ACCESS.PRODUCTS_SET_ENABLED),
      {
        method: API_ROUTES.AGENT_ACCESS.PRODUCTS_SET_ENABLED.method,
        headers: jsonHeaders(accessToken),
        body: JSON.stringify({ data: { agent_key: agentKey, enabled } })
      }
    )

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      const body = await readJsonBody<{ message?: string }>(response)
      throw new Error(
        body?.message ||
          `Failed to update agent product: ${response.status} ${response.statusText}`
      )
    }

    const json = (await response.json()) as { data?: unknown }
    if (json.data != null) validateAgentProductEnabled(json.data)
    return { success: true }
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'An unexpected error occurred'
    }
  }
}
